import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useProfile } from "@/context/ProfileContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Avatar from "@/components/Avatar";

export default function Quiz() {
  const { t, L, lang } = useI18n();
  const { options, setProfile, analyze } = useProfile();
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);

  if (!options) return <div className="min-h-[60vh] flex items-center justify-center text-charcoal/50">{t("common.loading")}</div>;

  const steps = [
    { key: "gender", type: "cards", opts: options.genders, q_it: "Qual è il tuo genere?", q_en: "What is your gender?" },
    { key: "age_group", type: "cards", opts: options.age_groups, q_it: "La tua fascia d'età", q_en: "Your age range" },
    { key: "skin_tone", type: "swatch", opts: options.skin_tones, q_it: "Scegli la tua tonalità di pelle", q_en: "Choose your skin tone" },
    { key: "undertone", type: "swatch", opts: options.undertones, q_it: "Qual è il tuo sottotono?", q_en: "What is your undertone?" },
    { key: "eye_color", type: "swatch", opts: options.eye_colors, q_it: "Il colore dei tuoi occhi", q_en: "Your eye color" },
    { key: "hair_color", type: "swatch", opts: options.hair_colors, q_it: "Il colore dei tuoi capelli", q_en: "Your hair color" },
    { key: "hair_length", type: "cards", opts: options.hair_lengths, q_it: "Lunghezza dei capelli", q_en: "Hair length" },
    { key: "face_shape", type: "cards", opts: options.face_shapes, q_it: "La forma del tuo viso", q_en: "Your face shape" },
    { key: "body_type", type: "cards", opts: options.body_types, q_it: "La tua corporatura", q_en: "Your body type" },
    { key: "skin_type", type: "cards", opts: options.skin_types, q_it: "Il tuo tipo di pelle", q_en: "Your skin type" },
  ];

  const cur = steps[step];
  const progress = ((step + 1) / steps.length) * 100;
  const value = data[cur.key];

  const select = (id) => {
    const nd = { ...data, [cur.key]: id };
    setData(nd);
    if (step < steps.length - 1) setTimeout(() => setStep(step + 1), 180);
  };

  const finish = async () => {
    setBusy(true);
    try {
      setProfile(data);
      await analyze(data);
      if (user) { try { await api.put("/profile", data); } catch (e) { console.warn("Profile save failed:", e); } }
      nav("/results");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] bg-surface">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-8">
        {/* progress */}
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => (step > 0 ? setStep(step - 1) : nav("/"))} data-testid="quiz-back" className="p-2 rounded-full hover:bg-white transition-colors"><ArrowLeft size={18} /></button>
          <div className="flex-1 h-1.5 bg-rose-100 rounded-full overflow-hidden">
            <motion.div className="h-full rose-gold-bg" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
          </div>
          <span className="text-sm text-charcoal/50 font-medium">{step + 1}/{steps.length}</span>
        </div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-rose-500 font-semibold mb-2">{t("quiz.title")}</p>
            <h1 className="font-serif text-3xl sm:text-4xl mb-8">{lang === "it" ? cur.q_it : cur.q_en}</h1>

            <AnimatePresence mode="wait">
              <motion.div key={cur.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
                {cur.type === "cards" ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`quiz-step-${cur.key}`}>
                    {cur.opts.map((o) => (
                      <button key={o.id} onClick={() => select(o.id)} data-testid={`opt-${o.id}`}
                        className={`relative text-left p-5 rounded-xl border transition-all hover:-translate-y-0.5 bg-white ${value === o.id ? "border-rose-400 ring-2 ring-rose-200" : "border-rose-100 hover:border-rose-300"}`}>
                        {o.hex && <span className="block w-8 h-8 rounded-full mb-3" style={{ background: o.hex }} />}
                        <span className="font-medium">{L(o, "label")}</span>
                        {value === o.id && <Check size={16} className="absolute top-3 right-3 text-rose-500" />}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-5 sm:grid-cols-8 gap-2.5 max-h-[420px] overflow-y-auto pr-2" data-testid={`quiz-step-${cur.key}`}>
                    {cur.opts.map((o) => (
                      <button key={o.id} onClick={() => select(o.id)} title={L(o, "label")} data-testid={`opt-${o.id}`}
                        className={`group relative aspect-square rounded-lg transition-all hover:scale-105 ${value === o.id ? "ring-2 ring-offset-2 ring-rose-500" : "ring-1 ring-rose-100"}`}
                        style={{ background: o.hex }}>
                        {value === o.id && <Check size={16} className="absolute inset-0 m-auto text-white drop-shadow" />}
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {step === steps.length - 1 && (
              <button onClick={finish} disabled={!value || busy} data-testid="quiz-finish" className="mt-8 inline-flex items-center gap-2 rounded-full bg-rose-400 hover:bg-rose-500 text-white font-semibold px-8 py-3.5 transition-all disabled:opacity-50">
                {busy ? "..." : t("common.results")} <ArrowRight size={18} />
              </button>
            )}
          </div>

          {/* live avatar */}
          <div className="hidden lg:block sticky top-24">
            <div className="bg-white rounded-2xl border border-rose-100 p-5 text-center">
              <p className="text-xs uppercase tracking-widest text-charcoal/40 mb-3">Il tuo avatar</p>
              <Avatar profile={data} options={options} size={260} />
              <p className="mt-3 text-sm text-charcoal/50">Si aggiorna in tempo reale</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
